import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Lien invalide."); return; }
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studio-bg grain min-h-screen grid place-items-center px-5 py-10 relative">
      <div className="relative z-10 w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3ECF8E] to-emerald-600 grid place-items-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-xl font-extrabold">Vira<span className="text-[#3ECF8E]">lyne</span></span>
        </Link>

        <div className="glass rounded-3xl p-8" data-testid="reset-password-card">
          {done ? (
            <div className="text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" data-testid="reset-success" />
              <h1 className="font-display text-2xl font-bold mt-5">Mot de passe mis à jour</h1>
              <p className="text-slate-400 mt-2">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
              <Button onClick={() => navigate("/login")} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="reset-go-login">
                Se connecter
              </Button>
            </div>
          ) : !token ? (
            <div className="text-center">
              <h1 className="font-display text-2xl font-bold">Lien invalide</h1>
              <p className="text-slate-400 mt-2">Ce lien de réinitialisation est incomplet ou expiré.</p>
              <Button onClick={() => navigate("/forgot-password")} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold">
                Refaire une demande
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold">Nouveau mot de passe</h1>
              <p className="text-slate-400 mt-2 text-sm">Choisissez un nouveau mot de passe pour votre compte.</p>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div>
                  <Label className="text-slate-300">Nouveau mot de passe</Label>
                  <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="Min. 6 caractères" data-testid="reset-password-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Confirmer le mot de passe</Label>
                  <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="Retapez le mot de passe" data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="reset-submit-button">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Réinitialiser"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
